import React, { useCallback } from 'react';
import { Controller, FormProvider, useForm, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import styled from 'styled-components';
import { useAppDispatch } from '../../../../../redux/hooks';
import { closeModal } from '../../../../../redux/slices/modal';
import KubitModal from '../../../../KubitModal';
import SaveEntityHeader, {
  SaveEntityHeaderEnum,
} from '../../../../SaveEntityHeader';
import { saveMeasureSchema } from './validation';
import { defaultSaveMeasureFilterState, ISaveMeasureState } from './types';
import usePermission from '../../../../../utils/hooks/permissionHooks/usePermission';
import {
  IManageVersionEntityMutation,
  IManageVersionEntityMutationVariables,
  IMeasureFieldsFragment,
  INamedMeasureCreateMutation,
  INamedMeasureCreateMutationVariables,
  INamedMeasureEditMutation,
  INamedMeasureEditMutationVariables,
  ManagedEntityType,
  MeasureDisplayFormatKind,
  PermissionKey,
} from '../../../../../utils/apollo/graphqlSchema';
import { CommentField, DescriptionField, NameField } from '../../../../Form';
import printGraphqlErrors from '../../../../../utils/apollo/printGraphqlErrors';
import { useApolloClient, useMutation } from '@apollo/client';
import {
  CREATE_NAMED_MEASURE,
  EDIT_NAMED_MEASURE,
} from '../../../../../utils/apollo/measures';
import PermissionElementWrapper from '../../../../../pages/PermissionControls/PermissionElementWrapper';
import { PrimaryButton, SecondaryButton } from '../../../../Button';
import { ModalActions } from '../../../../KubitModal/KubitModalUI';
import { prepareBasicMeasureInput } from '../utils/namedMeasureInputs';
import {
  assertNotNil,
  ICreatableReactSelectOption,
} from '../../../../../utils/types';
import NamedMeasureField from './NamedMeasureField';
import SaveMeasureModalStrategy from './SaveMeasureModalStrategy';
import { IRadioGroupOption } from '../../../../RadioGroup/types';
import { ISaveMeasureInputs } from '../utils/types';
import {
  MeasureDisplayFormatCalculation,
  MeasureDisplayFormatCalculationUnit,
  MeasureDisplayFormatCalculationValue,
  MeasureDisplayFormatKindField,
  MeasureDisplayFormatPrecision,
  MeasureDisplayFormatUnit,
} from '../../../MeasureDisplayFormat';
import isNil from 'lodash/isNil';
import { SEARCH_TAGS } from '../../../../../utils/apollo/entitySelection/query';
import { ApplyTags } from '../../../../Categories';
import { useSaveCategories } from '../../../../../utils/categories';
import toastMessage from '../../../../../utils/toastMessage';
import { defaultMeasureDisplayFormatInput } from '../../../../../utils/builder/measures/measureDisplayFormat/utils';
import { MANAGE_VERSION_ENTITY } from '../../../../../utils/apollo/manageView/mutations';
import { defaultManagedOption } from '../../../../Categories/utils';
import { measureSaved } from '../../../../../utils/analytics/events/measure/events';
import { useAnalytics } from '../../../../../utils/analytics/useAnalytics';
import { UnreachableCaseError } from 'ts-essentials';
import useQuickAccessDictionaryViewState from '../../../../../redux/selectorHooks/useQuickAccessDictionaryViewState';
import ThinDivider from '../../../../ThinDivider';
import { MEASURE_NAME_MAX_LENGTH } from '../../../../../config/constants';

interface IProps {
  defaultValues: Partial<ISaveMeasureState>;
  defaultNamedMeasure: IMeasureFieldsFragment | null;
  schemaId: string;
  strategyOptions: IRadioGroupOption[];
  inputs: ISaveMeasureInputs;
}

const StyledThinDivider = styled(ThinDivider)`
  margin-bottom: 24px;
`;
const FormatContainer = styled.div`
  display: flex;
  flex-direction: column;
  row-gap: 12px;
  max-width: 240px;
  margin-bottom: 24px;
`;
const TagsContainer = styled.div`
  margin-bottom: 24px;
`;

const SaveMeasureModal = ({
  defaultValues,
  defaultNamedMeasure,
  schemaId,
  strategyOptions,
  inputs,
}: IProps) => {
  const dispatch = useAppDispatch();
  const client = useApolloClient();
  const { track } = useAnalytics();

  const { checkIsPermitted } = usePermission();

  const isPermittedToReadMeasure = checkIsPermitted({
    requiredPermissionKeys: [PermissionKey.MeasureRead],
    schemaId,
  });
  const isDictionarySidebarExpanded = useQuickAccessDictionaryViewState();

  const formMethods = useForm<ISaveMeasureState>({
    resolver: zodResolver(saveMeasureSchema),
    defaultValues: {
      ...defaultSaveMeasureFilterState,
      ...defaultValues,
    },
  });

  const {
    handleSubmit,
    control,
    formState: { isSubmitting, isValid },
    resetField,
  } = formMethods;

  const selectedStrategy = useWatch({
    name: 'strategy',
    control,
  });

  const formatKind = useWatch({
    name: 'format.kind',
    control,
  });
  const calculationUnit = useWatch({
    name: 'format.calculationUnit',
    control,
  });

  const handleClose = useCallback(() => {
    dispatch(closeModal());
  }, [dispatch]);

  const handleManagedTag = async (measureId: string) => {
    const res = await manageVersionedEntity({
      variables: {
        input: {
          entityId: measureId,
          entityType: ManagedEntityType.Measure,
          isManaged: true,
        },
      },
    });

    if (res.data?.manageVersionEntity?.errors) {
      printGraphqlErrors(res.data.manageVersionEntity.errors);
    }
  };

  const onCreate = async ({
    namedMeasureCreate,
  }: INamedMeasureCreateMutation) => {
    const { entity, errors } = namedMeasureCreate;

    if (printGraphqlErrors(errors)) {
      return;
    }

    if (entity) {
      toastMessage({
        message: `${entity.name} has been created`,
        type: 'info',
      });

      if (isEntityManaged && entity.version?.versionId) {
        await handleManagedTag(entity.version.versionId);
      }

      // refetch latest measures for dictionary sidebar
      if (isDictionarySidebarExpanded) {
        client.refetchQueries({
          include: [SEARCH_TAGS],
        });
      }

      handleClose();

      inputs.onSubmit(entity);
    }
  };

  const onEdit = ({ namedMeasureEdit }: INamedMeasureEditMutation) => {
    const { entity, errors } = namedMeasureEdit;

    if (printGraphqlErrors(errors)) {
      return;
    }

    if (entity) {
      toastMessage({
        message: `${entity.name} has been updated`,
        type: 'info',
      });

      if (isEntityManaged && entity.version?.versionId) {
        handleManagedTag(entity.version.versionId);
      }

      handleClose();

      inputs.onSubmit(entity);
    }
  };

  const [createNamedMeasure, { loading: createProcessing }] = useMutation<
    INamedMeasureCreateMutation,
    INamedMeasureCreateMutationVariables
  >(CREATE_NAMED_MEASURE, {
    onCompleted: onCreate,
  });
  const [editNamedMeasure, { loading: editProcessing }] = useMutation<
    INamedMeasureEditMutation,
    INamedMeasureEditMutationVariables
  >(EDIT_NAMED_MEASURE, {
    onCompleted: onEdit,
  });

  const [manageVersionedEntity] = useMutation<
    IManageVersionEntityMutation,
    IManageVersionEntityMutationVariables
  >(MANAGE_VERSION_ENTITY);

  const {
    categories,
    setCategories,
    prepareCategories,
    isEntityManaged,
    setIsEntityManaged,
  } = useSaveCategories(schemaId);

  const onSubmitForm = handleSubmit(async (formData) => {
    const { strategy, name, comment, description, versionId, format } =
      formData;

    const basicMeasureInput = prepareBasicMeasureInput(
      inputs,
      schemaId,
      format,
    );

    const inputCategories = await prepareCategories();

    let savedMeasure: IMeasureFieldsFragment;

    if (strategy === 'create') {
      const result = await createNamedMeasure({
        variables: {
          input: {
            comment,
            description,
            measure: basicMeasureInput,
            name,
            schema: schemaId,
            categories: inputCategories,
          },
        },
      });
      const createdMeasure = result.data?.namedMeasureCreate.entity;
      assertNotNil(createdMeasure);
      savedMeasure = createdMeasure;
    } else if (strategy === 'update') {
      assertNotNil(versionId);

      const result = await editNamedMeasure({
        variables: {
          input: {
            name: null,
            comment,
            description,
            measure: basicMeasureInput,
            namedMeasureId: versionId,
            categories: inputCategories,
          },
        },
      });

      const updatedMeasure = result.data?.namedMeasureEdit.entity;
      assertNotNil(updatedMeasure);
      savedMeasure = updatedMeasure;
    } else {
      throw new UnreachableCaseError(strategy);
    }

    track(measureSaved(savedMeasure));
  });

  const handleChangeFormatKind = (newKind: MeasureDisplayFormatKind) => {
    // Change of a format kind will reset format
    resetField('format', {
      defaultValue: {
        ...defaultMeasureDisplayFormatInput,
        kind: newKind,
      },
    });
  };

  const handleChangeTags = (values: ICreatableReactSelectOption[]) => {
    const managed = values.find(
      (value) => value.value === defaultManagedOption.value,
    );
    setIsEntityManaged(!!managed);
    setCategories(
      values.filter((value) => value.value !== defaultManagedOption.value),
    );
  };

  const submitGreyedOut = !isValid;
  const submitDisabled = isSubmitting || !isValid;
  const inputsDisabled = createProcessing || editProcessing || isSubmitting;

  return (
    <KubitModal header={'Save Measure'} onClose={handleClose} open>
      <SaveEntityHeader entityType={SaveEntityHeaderEnum.NamedMeasure} />
      <FormProvider {...formMethods}>
        <form onSubmit={onSubmitForm}>
          <SaveMeasureModalStrategy strategyOptions={strategyOptions} />
          {selectedStrategy === 'create' ? (
            <Controller
              name="name"
              control={control}
              render={({ field, fieldState }) => (
                <NameField
                  label={'Name'}
                  value={field.value}
                  onChange={field.onChange}
                  placeholder={'Enter a new measure name'}
                  targetErrorElement="Measure"
                  disabled={createProcessing || isSubmitting}
                  error={fieldState.error?.message}
                  maxLength={MEASURE_NAME_MAX_LENGTH}
                />
              )}
            />
          ) : (
            <NamedMeasureField
              defaultNamedMeasure={defaultNamedMeasure}
              isPermittedToReadMeasure={isPermittedToReadMeasure}
              categories={categories}
              setCategories={setCategories}
              setIsEntityManaged={setIsEntityManaged}
            />
          )}
          <Controller
            name="description"
            control={control}
            render={({ field, fieldState }) => (
              <DescriptionField
                label={'Description'}
                value={field.value || ''}
                onChange={field.onChange}
                placeholder={'Add measure description here'}
                targetErrorElement="Measure"
                disabled={inputsDisabled}
                error={fieldState.error?.message}
              />
            )}
          />
          <TagsContainer>
            <ApplyTags
              schemaId={schemaId}
              selectedValues={categories}
              onChange={handleChangeTags}
              isManaged={isEntityManaged}
            />
          </TagsContainer>
          <StyledThinDivider layout="horizontal" size="full" />
          <FormatContainer>
            <Controller
              name="format.kind"
              control={control}
              render={({ field }) => (
                <MeasureDisplayFormatKindField
                  value={field.value}
                  onChange={handleChangeFormatKind}
                />
              )}
            />
            <Controller
              name="format.decimalPlace"
              control={control}
              render={({ field }) => (
                <MeasureDisplayFormatPrecision
                  value={!isNil(field.value) ? field.value.toString() : null}
                  onChange={(newValue) => field.onChange(Number(newValue))}
                />
              )}
            />
            {!inputs.hideCalculation && (
              <MeasureDisplayFormatCalculation>
                <Controller
                  name="format.calculationUnit"
                  control={control}
                  render={({ field }) => (
                    <MeasureDisplayFormatCalculationUnit
                      value={field.value}
                      onChange={field.onChange}
                    />
                  )}
                />
                {calculationUnit && (
                  <Controller
                    name="format.calculationValue"
                    control={control}
                    render={({ field }) => (
                      <MeasureDisplayFormatCalculationValue
                        value={
                          !isNil(field.value) ? field.value.toString() : null
                        }
                        onChange={(newValue) =>
                          field.onChange(
                            !isNil(newValue) ? Number(newValue) : null,
                          )
                        }
                      />
                    )}
                  />
                )}
              </MeasureDisplayFormatCalculation>
            )}
            {formatKind === MeasureDisplayFormatKind.Number && (
              <Controller
                name="format.unit"
                control={control}
                render={({ field }) => (
                  <MeasureDisplayFormatUnit
                    value={field.value}
                    onChange={field.onChange}
                  />
                )}
              />
            )}
          </FormatContainer>
          <StyledThinDivider layout="horizontal" size="full" />
          <Controller
            name="comment"
            control={control}
            render={({ field }) => (
              <CommentField
                label={'Comment'}
                value={field.value || ''}
                onChange={field.onChange}
                placeholder={'Add your comment here'}
                targetErrorElement="Measure"
                disabled={inputsDisabled}
              />
            )}
          />
          <ModalActions>
            <SecondaryButton
              type="button"
              onClick={handleClose}
              disabled={inputsDisabled}
              trackContext="measure"
              trackAction="cancel"
            >
              Cancel
            </SecondaryButton>
            {selectedStrategy === 'create' ? (
              <PrimaryButton
                type="submit"
                isGreyedOut={submitGreyedOut}
                disabled={submitDisabled || createProcessing}
                trackContext="measure"
                trackAction="create"
              >
                Create
              </PrimaryButton>
            ) : (
              <PermissionElementWrapper isPermitted={isPermittedToReadMeasure}>
                {/* only need to check read permission, if update is not permitted, won't be able to access the page */}
                <PrimaryButton
                  type="submit"
                  isGreyedOut={submitGreyedOut}
                  disabled={
                    submitDisabled ||
                    editProcessing ||
                    !isPermittedToReadMeasure
                  }
                  trackContext="measure"
                  trackAction="update"
                >
                  Update
                </PrimaryButton>
              </PermissionElementWrapper>
            )}
          </ModalActions>
        </form>
      </FormProvider>
    </KubitModal>
  );
};

export default SaveMeasureModal;
